import React, { useEffect, useState, useCallback } from "react";
import { fetchJson } from "./api";

const GOLD = "#d4a550";
const GOLD_DIM = "rgba(212,165,80,0.10)";
const GOLD_BORDER = "rgba(212,165,80,0.28)";
const BG = "#07070a";
const SURFACE = "#0f0f17";
const BORDER = "rgba(255,255,255,0.07)";
const TEXT = "#e8e6df";
const TEXT_DIM = "rgba(232,230,223,0.45)";
const GREEN = "#4ade80";
const YELLOW = "#fbbf24";
const RED = "#f87171";

function StatusDot({ status }) {
  const color = status === "green" ? GREEN : status === "yellow" ? YELLOW : RED;
  const shouldBlink = status === "yellow" || status === "red";
  return (
    <span style={{
      display: "inline-block", width: 10, height: 10, borderRadius: "50%",
      background: color, flexShrink: 0,
      animation: shouldBlink ? "blink 1.2s ease-in-out infinite" : "none",
      boxShadow: shouldBlink ? `0 0 6px ${color}` : "none"
    }} />
  );
}

function getOverallStatus(productions) {
  if (!productions?.length) return "green";
  const statuses = productions.map(p => p.status);
  if (statuses.includes("red")) return "red";
  if (statuses.includes("yellow")) return "yellow";
  return "green";
}

export default function CoordinatorsPage({ token }) {
  const [coordinators, setCoordinators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchJson("/supervisor/coordinators");
      setCoordinators(data.coordinators || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function toggleCanSendAlerts(userId, current) {
    setToggling(t => ({ ...t, [userId]: true }));
    try {
      await fetchJson(`/supervisor/coordinators/${userId}/can_send_alerts`, {
        method: "PATCH",
        body: JSON.stringify({ can_send_alerts: !current }),
      });
      setCoordinators(cs => cs.map(c => c.id === userId ? { ...c, can_send_alerts: !current } : c));
    } finally {
      setToggling(t => ({ ...t, [userId]: false }));
    }
  }

  async function toggleApproved(userId, current) {
    setToggling(t => ({ ...t, [userId]: true }));
    try {
      await fetchJson(`/supervisor/coordinators/${userId}/approved`, {
        method: "PATCH",
        body: JSON.stringify({ approved: !current }),
      });
      setCoordinators(cs => cs.map(c => c.id === userId ? { ...c, approved: !current } : c));
    } catch (e) {
      alert(e.message);
    } finally {
      setToggling(t => ({ ...t, [userId]: false }));
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: BG, color: TEXT, fontFamily: "'Inter', sans-serif", padding: "32px 24px" }}>
      <style>{`
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.2; }
        }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 11, fontFamily: "'Kanit', sans-serif", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: GOLD, marginBottom: 6 }}>
          Gestione
        </div>
        <h1 style={{ margin: "0 0 8px 0", fontSize: 26, fontWeight: 800, color: TEXT }}>Coordinatori</h1>
        <p style={{ margin: 0, fontSize: 13, color: TEXT_DIM }}>
          Visualizza e gestisci i coordinatori della tua azienda.
        </p>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", color: TEXT_DIM, fontSize: 13, padding: 40 }}>Caricamento...</div>
      ) : coordinators.length === 0 ? (
        <div style={{ textAlign: "center", color: TEXT_DIM, fontSize: 13, padding: 40 }}>Nessun coordinatore registrato.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {/* Header tabella */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 80px 80px", gap: 16, padding: "8px 20px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: TEXT_DIM }}>
            <span>Coordinatore</span>
            <span>Produzioni attive</span>
            <span style={{ textAlign: "center" }}>Status</span>
            <span style={{ textAlign: "center" }}>Accesso</span>
          </div>

          {coordinators.map(c => {
            const status = getOverallStatus(c.productions);
            const isToggling = !!toggling[c.id];
            return (
              <div key={c.id} style={{
                display: "grid", gridTemplateColumns: "1fr 2fr 80px 80px 80px", gap: 16,
                padding: "16px 20px", background: SURFACE,
                border: `1px solid ${c.approved ? BORDER : "rgba(248,113,113,0.2)"}`,
                borderRadius: 10, alignItems: "center",
                opacity: c.approved ? 1 : 0.6,
              }}>
                {/* Nome */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>{c.nome} {c.cognome}</span>
                    {!c.approved && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.35)', color: YELLOW }}>
                        In attesa
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: TEXT_DIM, marginTop: 2 }}>{c.email}</div>
                </div>

                {/* Produzioni */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {c.productions?.length > 0 ? c.productions.map(p => (
                    <span key={p.id} style={{
                      fontSize: 11, padding: "2px 10px", borderRadius: 20,
                      background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`, color: GOLD
                    }}>{p.name}</span>
                  )) : (
                    <span style={{ fontSize: 11, color: TEXT_DIM }}>Nessuna produzione attiva</span>
                  )}
                </div>

                {/* Status */}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  {c.productions?.length > 0 ? <StatusDot status={status} /> : (
                    <span style={{ fontSize: 10, color: TEXT_DIM }}>—</span>
                  )}
                </div>

                {/* Toggle alert */}
                <div style={{ display: "flex", flexDirection:"column", alignItems: "center", gap:4 }}>
                  <div style={{ fontSize:9, color:'#8a8a9a', textTransform:'uppercase', letterSpacing:'0.05em' }}>Alert</div>
                  <div
                    onClick={() => !isToggling && toggleCanSendAlerts(c.id, c.can_send_alerts !== false)}
                    style={{
                      width: 36, height: 20, borderRadius: 10,
                      background: c.can_send_alerts !== false ? "rgba(74,222,128,0.2)" : "rgba(248,113,113,0.15)",
                      border: `1px solid ${c.can_send_alerts !== false ? "rgba(74,222,128,0.4)" : "rgba(248,113,113,0.3)"}`,
                      position: "relative", cursor: isToggling ? "wait" : "pointer",
                      transition: "background 0.2s",
                    }}>
                    <div style={{
                      position: "absolute", top: 3,
                      left: c.can_send_alerts !== false ? 18 : 3,
                      width: 14, height: 14, borderRadius: "50%",
                      background: c.can_send_alerts !== false ? "#4ade80" : "#f87171",
                      transition: "left 0.2s",
                    }} />
                  </div>
                </div>
                {/* Toggle accesso */}
                <div style={{ display: "flex", flexDirection:"column", alignItems: "center", gap:4 }}>
                  <div style={{ fontSize:9, color:'#8a8a9a', textTransform:'uppercase', letterSpacing:'0.05em' }}>Accesso</div>
                  <div
                    onClick={() => !isToggling && toggleApproved(c.id, c.approved)}
                    style={{
                      width: 36, height: 20, borderRadius: 10,
                      background: c.approved ? "rgba(74,222,128,0.2)" : "rgba(248,113,113,0.15)",
                      border: `1px solid ${c.approved ? "rgba(74,222,128,0.4)" : "rgba(248,113,113,0.3)"}`,
                      position: "relative", cursor: isToggling ? "wait" : "pointer",
                      transition: "background 0.2s",
                    }}>
                    <div style={{
                      position: "absolute", top: 3, left: c.approved ? 18 : 3,
                      width: 12, height: 12, borderRadius: "50%",
                      background: c.approved ? GREEN : RED,
                      transition: "left 0.2s",
                    }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
